import { useState, useEffect, useCallback } from 'react';
import { X, Trash2, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import type { Approval } from '../../types/approvals';
import { getAgentBySlug } from '../../utils/agents';
import { useApprovalStore } from '../../stores/approvalStore';
import StatusBadge from '../common/StatusBadge';

const STATUS_MAP: Record<string, 'success' | 'warning' | 'error' | 'info' | 'running'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  revision_requested: 'info',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  revision_requested: 'Revision Requested',
};

interface ApprovalDetailModalProps {
  approval: Approval;
  onClose: () => void;
}

export default function ApprovalDetailModal({ approval, onClose }: ApprovalDetailModalProps) {
  const updateApproval = useApprovalStore((s) => s.updateApproval);
  const deleteApproval = useApprovalStore((s) => s.deleteApproval);

  const [reviewerNotes, setReviewerNotes] = useState(approval.reviewer_notes || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const submitter = getAgentBySlug(approval.submitted_by);
  const approver = getAgentBySlug(approval.approver);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  async function handleStatusUpdate(status: Approval['status']) {
    setSaving(true);
    try {
      await updateApproval(approval.id, {
        status,
        reviewer_notes: reviewerNotes.trim() || null,
      });
      onClose();
    } catch (err) {
      console.error('Failed to update approval:', err);
      setSaving(false);
    }
  }

  async function handleSaveNotes() {
    setSaving(true);
    try {
      await updateApproval(approval.id, {
        reviewer_notes: reviewerNotes.trim() || null,
      });
      onClose();
    } catch (err) {
      console.error('Failed to save notes:', err);
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteApproval(approval.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete approval:', err);
    }
  }

  function formatTimestamp(ts: string | null): string {
    if (!ts) return '-';
    return format(new Date(ts), 'MMM d, yyyy h:mm a');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">Approval Details</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {/* Title */}
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title</label>
            <p className="text-text-primary text-sm">{approval.title}</p>
          </div>

          {/* Description */}
          {approval.description ? (
            <div>
              <label className="block text-sm text-text-secondary mb-1">Description</label>
              <p className="text-text-primary text-sm whitespace-pre-wrap">{approval.description}</p>
            </div>
          ) : null}

          {/* Status */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">Status</label>
            <StatusBadge
              status={STATUS_MAP[approval.status] || 'info'}
              label={STATUS_LABELS[approval.status] || approval.status}
              size="md"
            />
          </div>

          {/* People */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Submitted By</label>
              <p className="text-text-primary text-sm">
                {submitter ? `${submitter.name} (${submitter.shortName})` : approval.submitted_by}
              </p>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Approver</label>
              <p className="text-text-primary text-sm">
                {approval.approver === 'owner'
                  ? 'Owners (Aaron & Taylor)'
                  : approver
                    ? `${approver.name} (${approver.shortName})`
                    : approval.approver}
              </p>
            </div>
          </div>

          {/* Priority & Department */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Priority</label>
              <p className="text-text-primary text-sm capitalize">{approval.priority}</p>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <p className="text-text-primary text-sm">{approval.department || '-'}</p>
            </div>
          </div>

          {/* Linked Task */}
          {approval.linked_task_id ? (
            <div>
              <label className="block text-sm text-text-secondary mb-1">Linked Task</label>
              <p className="text-text-primary text-sm">Task #{approval.linked_task_id}</p>
            </div>
          ) : null}

          {/* Artifact Path */}
          {approval.artifact_path ? (
            <div>
              <label className="block text-sm text-text-secondary mb-1">Artifact</label>
              <p className="text-text-primary text-sm font-mono text-xs break-all">{approval.artifact_path}</p>
            </div>
          ) : null}

          {/* Reviewer Notes */}
          <div>
            <label className="block text-sm text-text-secondary mb-1">Reviewer Notes</label>
            <textarea
              value={reviewerNotes}
              onChange={(e) => setReviewerNotes(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
              placeholder="Add review notes..."
              rows={3}
            />
          </div>

          {/* Timestamps */}
          <div className="border-t border-border-subtle pt-3 mt-1">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <span className="text-text-muted block">Submitted</span>
                <span className="text-text-secondary">{formatTimestamp(approval.submitted_at)}</span>
              </div>
              <div>
                <span className="text-text-muted block">Reviewed</span>
                <span className="text-text-secondary">{formatTimestamp(approval.reviewed_at)}</span>
              </div>
              <div>
                <span className="text-text-muted block">Updated</span>
                <span className="text-text-secondary">{formatTimestamp(approval.updated_at)}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          {approval.status === 'pending' || approval.status === 'revision_requested' ? (
            <div className="flex gap-2 mt-1">
              <button
                onClick={() => handleStatusUpdate('approved')}
                disabled={saving}
                className="flex items-center gap-1.5 bg-success/15 text-success px-3 py-2 rounded-lg text-sm font-medium hover:bg-success/25 transition-colors disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" />
                Approve
              </button>
              <button
                onClick={() => handleStatusUpdate('rejected')}
                disabled={saving}
                className="flex items-center gap-1.5 bg-error/15 text-error px-3 py-2 rounded-lg text-sm font-medium hover:bg-error/25 transition-colors disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </button>
              <button
                onClick={() => handleStatusUpdate('revision_requested')}
                disabled={saving}
                className="flex items-center gap-1.5 bg-info/15 text-info px-3 py-2 rounded-lg text-sm font-medium hover:bg-info/25 transition-colors disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                Request Revision
              </button>
            </div>
          ) : null}

          {/* Footer: delete + save notes */}
          <div className="flex justify-between items-center mt-2">
            <button
              onClick={handleDelete}
              className={`flex items-center gap-1.5 text-sm transition-colors px-3 py-2 rounded-md ${
                confirmDelete
                  ? 'bg-error/15 text-error'
                  : 'text-text-muted hover:text-error'
              }`}
            >
              <Trash2 className="h-4 w-4" />
              {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={saving}
                className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
